export const english = {
  // App.js texts
  app: {
    title: "FrontPlay",
    subtitle: "HTML/CSS/JavaScript Playground",
    languageLabel: "Language:",
    templateLabel: "Code Type:",
    templatePlaceholder: "Choose an option",
    templateBasicHtml: "HTML/CSS/JS",
    templateReact: "React",
    templateCanvas: "Canvas",
    sampleLoad: "Load Sample",
    backToLandingTitle: "Back to home",
    toolsMenuTitle: "Tools menu",
    leaveEditorConfirm: "Leave the editor? Your code is kept for this session.",
    darkModeTitle: "Toggle Dark Mode",
    fullscreenTitle: "Fullscreen preview (browser-like)",
    fullscreenExitTitle: "Exit fullscreen (Esc)",
    verticalSplit: "Vertical Split",
    horizontalSplit: "Horizontal Split",
    codeEditor: "Code Editor",
    run: "Run",
    clear: "Clear",
    clearConfirm: "Clear the editor, preview, and console? This cannot be undone.",
    templateSwitchClearConfirm: "Switching code type will clear the current code. Continue?",
    find: "Find",
    findTitle: "Find in code",
    findPreviewTitle: "Find in preview",
    findPlaceholder: "Find in code…",
    findPlaceholderPreview: "Find in preview…",
    linkTitle: "Source mapping (HTML only: shows the selected element's source line)",
    hideEditorTitle: "Hide code editor",
    showEditorTitle: "Show code editor",
    undoTitle: "Undo",
    redoTitle: "Redo",
    findPrevTitle: "Previous (Shift+Enter)",
    findNextTitle: "Next (Enter)",
    findCloseTitle: "Close (Esc)",
    linkMatched: "Source line: {line} ({count} match)",
    linkNoMatch: "No source match (possibly dynamically generated)",
    preview: "Preview",
    devicePreset: "Device",
    deviceCustom: "Responsive",
    deviceSizeTitle: "Preview size",
    paperSkillLabel: "Paper",
    paperSkillNone: "No paper",
    paperSkillHint: "Adds @page CSS on download/print. The same setting is passed to AI edit.",
    paperSkills: {
      "a5-portrait": "A5 Portrait",
      "a5-landscape": "A5 Landscape",
      "a4-portrait": "A4 Portrait",
      "a4-landscape": "A4 Landscape",
      "a3-portrait": "A3 Portrait",
      "a3-landscape": "A3 Landscape",
      "letter-portrait": "Letter Portrait",
      "letter-landscape": "Letter Landscape"
    },
    print: "Print",
    printTitle: "Print with paper settings (same HTML as download)",
    printCaution: {
      title: "Margins may disappear when printing",
      itemPadding: "{selector}'s padding margin is removed at print time",
      itemMargin: "{selector}'s margin is removed at print time",
      fix: "Fix",
      dismiss: "Dismiss"
    },
    console: "Console",
    consoleOutput: "Console Output",
    codePlaceholder: "Write your HTML/CSS/JavaScript code here...",
    emptyCodePlaceholder: "Select a template to start coding...",
    download: "Download",
    import: "Upload",
    emptyPreview: {
      title: "Preview will appear here",
      description: "Write code in the editor, then click Run to see it here. You can also load a sample from the button above."
    },
    templateWelcome: "Welcome to FrontPlay!",
    templateDescription: "Write your HTML, CSS, and JavaScript here.",
    htmlTemplate: {
      buttonText: "Click me!",
      alertMessage: "Hello from JavaScript!",
      consoleMessage: "Button clicked!"
    },
    reactTemplate: {
      title: "React Counter",
      countLabel: "Count:"
    },
    canvasTemplate: {
      title: "Canvas Graphics",
      gradientComment: "Gradient background",
      circleComment: "Draw circle",
      consoleMessage: "FrontPlay - Canvas drawing complete!"
    },
    markdownTemplate: {
      title: "Welcome to FrontPlay Markdown!",
      description: "This is a **Markdown** sample.",
      featuresTitle: "Features",
      boldItalic: "**Bold** and *italic* text",
      link: "[Links](https://example.com)",
      codeBlock: "Code blocks",
      listTitle: "Lists",
      numberedList1: "Numbered list",
      numberedList2: "Item 2",
      subItem1: "Sub item",
      subItem2: "Another sub item",
      quote1: "This is a quote.",
      quote2: "Markdown makes it easy to create beautiful documents.",
      closing: "Enjoy Markdown with **FrontPlay**!"
    }
  },

  ai: {
    title: "AI Edit",
    instructionLabel: "What should change?",
    placeholder: "e.g. Make the button green and add a reset control",
    apply: "Apply with AI",
    applying: "Applying…",
    hint: "Watch the preview, then instruct changes. ⌘/Ctrl+Enter to apply. Undo restores previous code.",
    paperSkillLabel: "Paper skill",
    paperSkillNone: "None (default)",
    paperSkillHint: "Paper size for download/print. AI sizes content to fit one page.",
    paperSkills: {
      "a5-portrait": "A5 Portrait",
      "a5-landscape": "A5 Landscape",
      "a4-portrait": "A4 Portrait",
      "a4-landscape": "A4 Landscape",
      "a3-portrait": "A3 Portrait",
      "a3-landscape": "A3 Landscape",
      "letter-portrait": "Letter Portrait",
      "letter-landscape": "Letter Landscape"
    },
    proOnly: "AI Edit (Pro)",
    comingSoon: "Coming soon",
    loginRequired: "Log in to use",
    loginNote: "Log in to use AI Edit. Free includes up to 5 edits/month.",
    markdownNotSupported: "AI Edit supports HTML / React / Canvas. For Markdown, edit directly in the editor.",
    markdownNotSupportedShort: "AI Edit is not available for Markdown",
    paidPlanBadge: "Pro",
    upgradeNote: "Upgrade for a higher monthly allowance.",
    instructionRequired: "Enter an instruction",
    codeRequired: "Paste or write code first",
    requestFailed: "AI edit failed",
    invalidResponse: "Invalid AI response",
    remaining: "{pct}% used",
    quotaPercent: "{pct}% used",
    quotaOk: "{pct}% used",
    quotaNear: "{pct}% used",
    quotaExhausted: "This month's usage limit reached",
    viewUsage: "View usage"
  },

  usage: {
    title: "Usage",
    short: "Usage",
    subtitle: "How much of this month's allowance you have used.",
    currentPlan: "Current plan",
    percentLabel: "Used this month",
    exhausted: "You have reached this month's allowance.",
    resetOn: "Resets on (from plan start)",
    refresh: "Refresh",
    retry: "Retry",
    loading: "Loading…",
    loadFailed: "Failed to load usage. Check that the API server is running and Supabase is configured.",
    notConfigured: "Usage is unavailable",
    loginRequired: "Log in to view your usage."
  },

  billing: {
    plans: "Plans",
    upgrade: "Upgrade",
    comingSoon: "Coming soon",
    title: "Choose a plan",
    subtitle: "Pick a monthly AI edit allowance. Paid plans are coming soon.",
    upgradeToLight: "Upgrade to Pro",
    upgradeToPro: "Upgrade to Pro",
    upgradeToBusiness: "Upgrade to Business",
    currentPlan: "Current plan",
    manage: "Manage",
    manageSubscription: "Manage billing & cancel",
    processing: "Processing…",
    failed: "Could not start checkout",
    success: "Payment complete. Updating your plan…",
    canceled: "Checkout canceled.",
    downgradeViaPortal: "Downgrade or cancel from the billing portal."
  },

  auth: {
    login: "Log in",
    logout: "Log out",
    loginTitle: "Log in",
    magicLinkHint: "Enter your email address and we'll send you a login link.",
    sendLoginLink: "Send login link",
    sending: "Sending…",
    checkEmail: "Login link sent. Open the link in the email to log in.",
    sendFailed: "Failed to send login link",
    planFree: "Free",
    planLight: "Pro",
    planPro: "Pro",
    planBusiness: "Business"
  },

  admin: {
    title: "Admin",
    back: "Back",
    stats: "Stats",
    users: "Users",
    totalUsers: "Total users",
    monthEdits: "AI edits this month",
    freeBudget: "Free budget used",
    freeBudgetSetting: "Free monthly budget",
    save: "Save",
    refresh: "Refresh",
    email: "Email",
    plan: "Plan",
    usage: "Usage this month",
    created: "Created",
    actions: "Actions",
    resetQuota: "Reset quota",
    adminTag: "admin",
    cannotChangeAdmin: "Cannot change admin plan",
    noUsers: "No users",
    loading: "Loading…",
    notConfigured: "Admin API is not configured"
  },

  // LandingPage.js texts
  landing: {
    title: "FrontPlay",
    subtitle1: "AI Chat Generated Code",
    subtitle2: "Execution Environment",
    description1: "Take code from ChatGPT, Claude, Gemini, and more — ",
    description2: "verify it safely",
    description3: ", then deliver it as a finished artifact.",
    description4: "",
    getStartedButton: "Get Started",
    featuresButton: "View Features",
    nav: {
      useCases: "Use Cases",
      features: "Features",
      security: "Security",
      plans: "Pricing"
    },
    backToTop: "Back to top",
    securityTitle: "Enterprise-Grade Security Built-In",
    securitySubtitle: "Multi-layered defense system that completely protects your system from malicious code",
    sectionTitle: "Professional Features",
    sectionSubtitle: "Rich features that balance security and convenience",
    useCasesTitle: "When to use it",
    useCasesSubtitle: "Verify. Then deliver.",
    ctaTitle: "Start by running it",
    ctaDescription1: "Paste generated code and begin verifying right away.",
    ctaDescription2: "",
    ctaButton: "Try FrontPlay",
    footerTitle: "FrontPlay",
    footerDescription: "Safe execution environment for AI-generated code",
    features: {
      enterpriseSecurity: {
        title: "Enterprise-Grade Security",
        description: "Complete system protection from malicious code through sandboxing, API restrictions, and multi-layered defense systems."
      },
      aiCodeExecution: {
        title: "AI Code Execution",
        description: "Paste and instantly execute code generated by ChatGPT, Claude, Gemini directly."
      },
      realtimeValidation: {
        title: "Real-time Validation",
        description: "Instantly verify if AI-generated code works as expected and validate quickly."
      },
      safeExecution: {
        title: "Safe Execution",
        description: "Fine-tune and improve AI code, then safely verify results with the run button."
      },
      debugSupport: {
        title: "Console & Network Visibility",
        description: "View Console / Network / Errors side-by-side. Every request is logged with direction, method, URL, and a warning badge when sensitive data is suspected."
      },
      multiFormat: {
        title: "Multi-Format Support",
        description: "Supports various formats of AI-generated code including HTML, React, Canvas, and more."
      }
    },
    securityFeatures: {
      sandbox: {
        title: "Sandbox Execution",
        description: "Runs in a fully isolated opaque-origin iframe, blocking unauthorized access to the parent page and local storage"
      },
      apiRestriction: {
        title: "Network Visibility + Send Confirmation",
        description: "Inbound (GET/HEAD/resource loads) allowed + fully logged; outbound (POST/PUT/WebSocket/etc.) asks the user per request. sendBeacon blocked by default."
      },
      realtimeMonitoring: {
        title: "Whitelist + CSP Defense-in-depth",
        description: "Allowed domains are persisted and auto-approved next run. Browser-level CSP plus JS hooks form a multi-layer defense against future APIs and hook bypass."
      }
    },
    useCases: {
      isolatedRun: {
        title: "Run untrusted code in isolation",
        description: "With AI-generated code, you can't tell what it does in the background until you run it. FrontPlay runs it in a “safe lab” that never touches your computer or files, so you can verify behavior without risking your environment."
      },
      networkCheck: {
        title: "See where data is sent — before you decide",
        description: "Even if it looks correct, where data goes in the background is a separate question. Every outbound request triggers a confirmation dialog, with warnings when sensitive data may be involved — so you can review the destination and approve sending yourself."
      },
      codeDeliverable: {
        title: "Fix what you can yourself, leave the rest to AI",
        description: "For small tweaks, edit directly and skip the AI cost. For complex changes, just tell the AI in words. Either way, you see the result on the spot."
      },
      printShare: {
        title: "Beyond running on screen — make it printable and shareable",
        description: "Verifying behavior isn't the end. Export to PDF matched to a paper size, and it's ready to print or hand out as material."
      }
    },
    footerSections: {
      security: "Security Features",
      sandboxExecution: "Sandbox Execution",
      apiRestrictionSystem: "Network Visibility System",
      multiLayerDefense: "Send Confirm + Whitelist",
      formatSupport: "Formats"
    },
    codeExample: {
      title: "Hello, FrontPlay!",
      description: "Express your ideas through code",
      buttonText: "Click me!"
    },
    plans: {
      title: "Pricing Plans",
      subtitle: "All core features are free. Pick the AI allowance that fits how you work.",
      free: {
        name: "Free",
        priceLabel: "$0",
        features: [
          "Run, edit & preview code in the sandbox",
          "Console / Network / Errors visibility",
          "Send confirmation & domain whitelist",
          "AI Edit: up to 5 requests / month (login required)"
        ],
        cta: "Get Started Free"
      },
      light: {
        name: "Pro",
        badge: "Model included",
        priceLabel: "$2/mo",
        features: [
          "Everything in Free (core features are free)",
          "AI Edit: up to 50 requests / month",
          "Model cost included — no key needed"
        ],
        cta: "Coming soon"
      },
      business: {
        name: "Business",
        badge: "Contact us",
        priceLabel: "Contact us",
        features: [
          "Everything in Free (core features are free)",
          "Custom plans for teams & orgs",
          "Tailored quote & priority support"
        ],
        cta: "Contact sales"
      }
    }
  },

  // Network visibility + send-confirm UI texts
  network: {
    consoleTab: "Console",
    networkTab: "Network",
    errorsTab: "Errors",
    consoleEmpty: "No console output",
    errorsEmpty: "No errors",
    empty: "No network activity",
    method: "Method",
    direction: "Dir",
    url: "URL",
    status: "Status",
    sensitiveTip: "May contain sensitive data",
    highEntropyTip: "High-entropy value detected",
    cspTip: "Blocked by CSP",
    confirmTitle: "Outbound data transfer detected",
    confirmSubtitle: "Do you want to allow this request?",
    bodyLabel: "Body (preview)",
    allowOnce: "Allow once",
    allowAlways: "Always allow",
    deny: "Deny",
    closeAria: "Close",
    whitelistTitle: "Network Whitelist",
    whitelistSubtitle: "Domains allowed here skip the confirmation modal in future runs.",
    domain: "Domain",
    send: "Send",
    beacon: "Beacon",
    add: "Add",
    whitelistEmpty: "No allowed domains yet",
    whitelistPlaceholder: "example.com"
  },

  // SecurityWarning.js texts
  security: {
    title: "Important Security Notice",
    warningTitle: "Code Execution Precautions",
    securityTitle: "FrontPlay's Security Measures",
    disclaimer: "Disclaimer:",
    disclaimerText: "While FrontPlay provides a secure execution environment, users are responsible for the content of the code they execute. Execute unknown or suspicious code at your own risk.",
    agreementText: "I understand the above precautions and agree to execute code safely",
    acceptButton: "Understood, Start",
    cancelButton: "Cancel",
    warnings: [
      "Only execute code generated by trusted AI (ChatGPT, Claude, Gemini, etc.)",
      "Do not execute code containing personal information, passwords, API keys, or other sensitive data",
      "Avoid executing malicious or unknown code",
      "Pay special attention to code that performs network communications"
    ],
    securityMeasures: [
      "Execution within sandboxed iframe",
      "Restricted access to external resources",
      "No data persistence (browser-only)",
      "Restricted access to parent page",
      "Restricted local storage access"
    ]
  }
};